import type { WorkspaceKey } from '@festina-life/api'
import { assertNotEmpty, type Optional } from '@festina-life/core'
import { Button } from '@festina-life/ui/actions'
import type { ReactNode } from 'react'
import { useNavigate } from 'react-router'

import { useTransferFromRouterLocation } from '../../hooks/useTransferFromRouterLocation'
import { configurationItem } from '../../system/paths'
import { useNavigationParams } from '../../system/useNavigationParams'
import type { ConfigurationItem } from '../../types/common'
import { TransferConfirmationModal } from '../transferConfirmationModal/TransferConfirmationModal'

/** Transfer function bound to the source block and the picked destination block. */
type BoundTransfer = ReturnType<
  typeof useTransferFromRouterLocation
>['onTransfer']

/**
 * Copies the operations of the configuration item in scope to the destination block.
 * Which operations are transferred, and in how many steps, differs per configuration
 * item, so each item provides its own confirmation handler.
 */
export type TransferConfirmation = (props: {
  destinationBlock: string
  transfer: BoundTransfer
}) => Promise<Optional<WorkspaceKey>>

/** Configuration item whose versions are transferred. */
type TransferredItem = {
  /** Item to navigate to once the transfer succeeds, usually the `operationName`. */
  itemId: string
  type: ConfigurationItem
  /** Item the versions belong to, used to title the modal. */
  operationName: string
  /** Number of versions the transfer covers, shown in the modal. */
  versionCount: number
  /** Whether anything can be transferred. The action is hidden when nothing can. */
  isTransferable: boolean
}

/** How the transfer is offered to, and confirmed by, the user. */
type TransferAction = {
  /** Translated label of the action, naming the item being transferred. */
  label: string
  description?: string
  /**
   * Translated warning about an operation that already exists on the destination
   * block, resolved for the block currently picked in the modal.
   */
  getConflictWarning?: (destinationBlock: string) => Optional<string>
  /** Called before the modal opens, for instance to reset the scope it offers. */
  onOpen?: () => void
  onConfirm: TransferConfirmation
}

type Props = {
  item: TransferredItem
  transfer: TransferAction
  /** Rendered inside the modal, for instance to pick the scope of the transfer. */
  children?: ReactNode
}

/**
 * Transfers the configuration item in scope to an ancestor block, which annuls it on
 * the block it is transferred from. This is how a configuration item is annulled, so
 * every item type supporting annulment offers this action.
 */
export const TransferButton = ({
  item: { itemId, type, operationName, versionCount, isTransferable },
  transfer: { label, description, getConflictWarning, onOpen, onConfirm },
  children,
}: Props) => {
  const navigate = useNavigate()
  const { branch, block, server, layer } = useNavigationParams()

  const {
    ancestorsForViewBlock,
    isTransferModalOpen,
    setIsTransferModalOpen,
    setSelectedDestinationBlock,
    selectedDestinationBlock,
    isSingleOrNoWorkspaceBranch,
    currentWorkspaceKey,
    onTransfer,
    isHidden,
  } = useTransferFromRouterLocation()

  const canOperationsBeTransferredToWorkspace =
    !!currentWorkspaceKey || isSingleOrNoWorkspaceBranch

  if (isHidden || !isTransferable) {
    return null
  }

  const handleTransfer = async () => {
    assertNotEmpty(selectedDestinationBlock)

    const addedToWorkspaceKey = await onConfirm({
      destinationBlock: selectedDestinationBlock,
      transfer: onTransfer,
    })

    setIsTransferModalOpen(false)
    setSelectedDestinationBlock(undefined)

    if (addedToWorkspaceKey) {
      // The item is annulled on the block it was transferred from, so it is only
      // visible on the destination block from here on.
      await navigate(
        configurationItem({
          block: selectedDestinationBlock,
          branch,
          server,
          layer,
          itemId,
          type,
          workspaceName: addedToWorkspaceKey.name,
        }),
      )
    }
  }

  const handleOpenTransferModal = () => {
    onOpen?.()
    setIsTransferModalOpen(true)
  }

  return (
    <>
      <TransferConfirmationModal
        isOpen={isTransferModalOpen}
        onAccept={handleTransfer}
        onCancel={() => setIsTransferModalOpen(false)}
        operationName={operationName}
        versionCount={versionCount}
        sourceBlock={block}
        description={description}
        conflictWarning={
          (selectedDestinationBlock &&
            getConflictWarning?.(selectedDestinationBlock)) ||
          undefined
        }
        ancestors={ancestorsForViewBlock}
        ancestorValue={selectedDestinationBlock}
        onChangeAncestor={setSelectedDestinationBlock}
      >
        {children}
      </TransferConfirmationModal>
      <Button
        startIcon="ArrowRightCircle"
        onClick={handleOpenTransferModal}
        disabled={!canOperationsBeTransferredToWorkspace}
      >
        {label}
      </Button>
    </>
  )
}
